import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/AuthContext'
import AuthPage from './pages/AuthPage'
import WorkshopOnboardingPage from './pages/WorkshopOnboardingPage'
import SubscribePage from './pages/SubscribePage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AcceptInvitePage from './pages/AcceptInvitePage'
import LegalPage from './pages/LegalPage'
import BookingPage from './pages/BookingPage'
import ReviewPreferencesPage from './pages/ReviewPreferencesPage'
import QuoteApprovalPage from './pages/QuoteApprovalPage'
import GeneralAdditionalWorkApprovalPage from './pages/GeneralAdditionalWorkApprovalPage'
import GeneralExtraServiceLineApprovalPage from './pages/GeneralExtraServiceLineApprovalPage'
import SuspensionExtraServiceLineApprovalPage from './pages/SuspensionExtraServiceLineApprovalPage'
import PaymentConfirmationPage from './pages/PaymentConfirmationPage'
import CustomerPaymentPage from './pages/CustomerPaymentPage'
import PortalAccessPage from './pages/PortalAccessPage'
import JobTagRedirectPage from './pages/JobTagRedirectPage'
import RiderPortalPage from './pages/RiderPortalPage'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Customers from './pages/Customers'
import Jobs from './pages/Jobs'
import Payments from './pages/Payments'
import Reporting from './pages/Reporting'
import CapacityDelays from './pages/CapacityDelays'
import JobTime from './pages/JobTime'
import JobProfitability from './pages/JobProfitability'
import ReportingBooking from './pages/ReportingBooking'
import FinancialRecords from './pages/FinancialRecords'
import LegacyImport from './pages/LegacyImport'
import TabletMode from './pages/TabletMode'
import ProfilesSetup from './pages/ProfilesSetup'
import Settings from './pages/Settings'
import VatDocuments from './pages/VatDocuments'
import AdminLayout from './pages/admin/AdminLayout'
import AdminDashboard from './pages/admin/AdminDashboard'
import { hasPortalMagicIntent, readPortalMagicIntent } from './lib/portalAuth'

function portalMagicRedirect(location) {
  if (location.pathname !== '/') return ''
  const hash = window.location.hash || ''
  if (hash.includes('type=recovery') || hash.includes('type=invite')) return ''

  const hashParams = new URLSearchParams(hash.replace(/^#/, ''))
  const searchParams = new URLSearchParams(location.search || '')
  const isMagicHash = hashParams.get('type') === 'magiclink'
  const hasPortalAuthCode = searchParams.has('code') && hasPortalMagicIntent()
  if (!isMagicHash && !hasPortalAuthCode) return ''

  return `${readPortalMagicIntent()}${location.search || ''}${hash}`
}

function PrivateRoute({ children }) {
  const { user, profile, loading, isActive, isSuperAdmin } = useAuth()
  const location = useLocation()

  // Recovery and invite tokens arrive as URL hash fragments on the root path.
  // AuthContext fires onAuthStateChange which sets a session — but before
  // fetchProfile completes, PrivateRoute would redirect to /subscribe.
  // Detect these flows via the hash and hold on the spinner instead.
  const hash = window.location.hash
  if (hash.includes('type=recovery')) return <Navigate to={`/reset-password${hash}`} replace />
  if (hash.includes('type=invite')) return <Navigate to={`/accept-invite${hash}`} replace />
  const portalRedirect = portalMagicRedirect(location)
  if (portalRedirect) return <Navigate to={portalRedirect} replace />

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span className="spinner" style={{ width: 24, height: 24 }} />
    </div>
  )

  if (!user) {
    const next = `${location.pathname}${location.search || ''}`
    const loginPath = next && next !== '/' ? `/login?next=${encodeURIComponent(next)}` : '/login'
    return <Navigate to={loginPath} replace />
  }
  if (!profile) return <Navigate to="/setup-workshop" replace />
  if (isSuperAdmin) return children
  if (!isActive) return <Navigate to="/subscribe" replace />
  return children
}

function AdminRoute({ children }) {
  const { user, loading, isSuperAdmin } = useAuth()
  const location = useLocation()
  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span className="spinner" style={{ width: 24, height: 24 }} /></div>
  if (!user) {
    const next = `${location.pathname}${location.search || ''}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }
  if (!isSuperAdmin) return <Navigate to="/" replace />
  return children
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<AuthPage />} />
          <Route path="/setup-workshop" element={<WorkshopOnboardingPage />} />
          <Route path="/subscribe" element={<SubscribePage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/book/:workshopSlug" element={<BookingPage />} />
          <Route path="/review-preferences" element={<ReviewPreferencesPage />} />
          <Route path="/quote/:token" element={<QuoteApprovalPage />} />
          <Route path="/general-additional-work/:token" element={<GeneralAdditionalWorkApprovalPage />} />
          <Route path="/general-extra-service-line/:token" element={<GeneralExtraServiceLineApprovalPage />} />
          <Route path="/suspension-extra-service-line/:token" element={<SuspensionExtraServiceLineApprovalPage />} />
          <Route path="/payment/complete" element={<PaymentConfirmationPage />} />
          <Route path="/pay" element={<CustomerPaymentPage />} />
          <Route path="/portal/access/:token" element={<PortalAccessPage />} />
          <Route path="/job-tag/:token" element={<JobTagRedirectPage />} />
          <Route path="/portal/*" element={<RiderPortalPage />} />
          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<AdminDashboard />} />
          </Route>
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="calendar" element={<Dashboard initialCalendarOpen />} />
            <Route path="customers" element={<Customers />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:jobId/time" element={<JobTime />} />
            <Route path="payments" element={<Payments />} />
            <Route path="reporting" element={<Reporting />} />
            <Route path="reporting/capacity" element={<CapacityDelays />} />
            <Route path="reporting/profitability" element={<JobProfitability />} />
            <Route path="reporting/bookings/:bookingId" element={<ReportingBooking />} />
            <Route path="financial-records" element={<FinancialRecords />} />
            <Route path="import" element={<LegacyImport />} />
            <Route path="profiles" element={<ProfilesSetup />} />
            <Route path="tablet" element={<TabletMode />} />
            <Route path="settings" element={<Settings />} />
            <Route path="vat-documents/:jobId" element={<VatDocuments />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
